import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight, LogOut, Settings, ChevronDown, KeyRound } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useSidebarStore } from '@/stores/sidebar-store';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ArcConnectionStatus } from '@/components/ArcConnectionStatus';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { navItems, systemNavItems, type NavItem } from '@/config/navigation';
import { usePermissions } from '@/hooks/usePermissions';
import { useArcStore } from '@/stores/arc-store';
import { useEmergencyStore } from '@/stores/emergency-store';
import { cn } from '@/lib/utils';

export function Sidebar() {
  const { profile, signOut } = useAuthStore();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { collapsed, toggle, autoExpanded, setAutoExpanded } = useSidebarStore();
  const { can, canAny, hasRole } = usePermissions();
  const location = useLocation();

  // Alert indicators for Emergency and Centro ARC
  const arcEvents = useArcStore((s) => s.events);
  const activeAlerts = useEmergencyStore((s) => s.activeAlerts);
  const hasCriticalArc = arcEvents.some((e) => !e.acknowledged_at && (e.severity === 'CRITICAL' || e.severity === 'ALARM'));
  const hasPendingAlert = activeAlerts.some((a) => a.status === 'PENDING' || a.status === 'ASSIGNED');
  const alertDot = (to: string) => {
    if (to === '/emergency' && hasPendingAlert && !location.pathname.startsWith('/emergency')) return true;
    if (to === '/arc/events' && hasCriticalArc && !location.pathname.startsWith('/arc')) return true;
    return false;
  };

  // Visibility predicate: hide item if it requires a permission the user lacks
  // OR if it requires super_admin and the user isn't one.
  const itemVisible = (item: NavItem) => {
    if (item.requireSuperAdmin && !hasRole('super_admin')) return false;
    if (item.requiredPermission && !can(item.requiredPermission)) return false;
    if (item.requiredAnyPermission && !canAny(item.requiredAnyPermission)) return false;
    return true;
  };
  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    // Auto-expand parent if current route matches a child (by exact path or activePrefix)
    const expanded: string[] = [];
    navItems.forEach((item) => {
      if (item.children?.some((child) =>
        location.pathname.startsWith(child.to) ||
        (child.activePrefix && location.pathname.startsWith(child.activePrefix))
      )) {
        expanded.push(item.to);
      }
    });
    return expanded;
  });

  const toggleExpanded = (to: string) => {
    setExpandedItems((prev) =>
      prev.includes(to) ? prev.filter((t) => t !== to) : [...prev, to]
    );
  };

  // Auto-expand parent groups when the active route changes (e.g. navigating via page links)
  useEffect(() => {
    const toExpand: string[] = [];
    navItems.forEach((item) => {
      if (item.children?.some((child) =>
        location.pathname.startsWith(child.to) ||
        (child.activePrefix && location.pathname.startsWith(child.activePrefix))
      )) {
        toExpand.push(item.to);
      }
    });
    if (toExpand.length > 0) {
      setExpandedItems((prev) => {
        const next = new Set([...prev, ...toExpand]);
        return Array.from(next);
      });
    }
  }, [location.pathname]);

  const isChildActive = (item: NavItem) =>
    item.children?.some((child) =>
      location.pathname === child.to ||
      location.pathname.startsWith(child.to + '/') ||
      (child.activePrefix && location.pathname.startsWith(child.activePrefix))
    );

  const renderNavItem = (item: NavItem) => {
    // Hide item if user lacks the required permission or super-admin role
    if (!itemVisible(item)) return null;

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.to);
    const childActive = isChildActive(item);

    if (hasChildren) {
      return (
        <div key={item.to}>
          <button
            onClick={() => {
              if (collapsed) {
                // Auto-expand sidebar when collapsed and clicking on parent with children
                toggle();
                toggleExpanded(item.to);
                setAutoExpanded(true); // Mark as auto-expanded
              } else {
                toggleExpanded(item.to);
              }
            }}
            title={collapsed ? item.label : undefined}
            className={cn(
              'w-full group flex items-center rounded-lg transition-all duration-150',
              collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
              childActive
                ? 'bg-[#1c2a52] text-amber-400'
                : 'text-slate-300/70 hover:bg-[#1c2a52] hover:text-slate-100'
            )}
          >
            <item.icon
              className={cn(
                'w-5 h-5 shrink-0 transition-colors',
                childActive ? 'text-amber-400' : 'text-slate-400 group-hover:text-slate-100'
              )}
            />
            {!collapsed && (
              <>
                <span className="text-sm truncate flex-1 text-left">{item.label}</span>
                <ChevronDown
                  className={cn(
                    'w-4 h-4 transition-transform duration-200',
                    isExpanded ? 'rotate-180' : ''
                  )}
                />
              </>
            )}
          </button>
          {/* Submenu */}
          {!collapsed && isExpanded && (
            <div className="mt-1 ml-4 pl-3 border-l border-[#2a3a5c] space-y-0.5">
              {item.children?.filter(itemVisible).map((child) => {
                const isChildItemActive =
                  location.pathname === child.to ||
                  location.pathname.startsWith(child.to + '/') ||
                  (!!child.activePrefix && location.pathname.startsWith(child.activePrefix));
                return (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    onClick={() => {
                      // Only auto-collapse if sidebar was auto-expanded (not if user manually expanded it)
                      if (autoExpanded) {
                        setTimeout(() => {
                          toggle();
                          setAutoExpanded(false);
                        }, 300);
                      }
                    }}
                    className={cn(
                      'group flex items-center gap-2 rounded-lg px-3 py-2 transition-all duration-150',
                      isChildItemActive
                        ? 'bg-[hsl(43,82%,46%)] text-[hsl(228,60%,15%)] font-semibold shadow-sm'
                        : alertDot(child.to)
                          ? 'bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 border border-red-500/20'
                          : 'text-slate-300/70 hover:bg-[#1c2a52] hover:text-slate-100'
                    )}
                  >
                    <child.icon
                      className={cn(
                        'w-4 h-4 shrink-0 transition-colors',
                        isChildItemActive ? 'text-[hsl(228,60%,15%)]'
                          : alertDot(child.to) ? 'text-red-400'
                          : 'text-slate-400 group-hover:text-slate-100'
                      )}
                    />
                    <span className="text-sm truncate flex-1">{child.label}</span>
                    {alertDot(child.to) && (
                      <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                    )}
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === '/'}
        title={collapsed ? item.label : undefined}
        className={() => {
          const isActive =
            location.pathname === item.to ||
            location.pathname.startsWith(item.to + '/') ||
            !!item.activePaths?.some((p) => location.pathname.startsWith(p));
          return cn(
            'group flex items-center rounded-lg transition-all duration-150',
            collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
            isActive
              ? 'bg-[hsl(43,82%,46%)] text-[hsl(228,60%,15%)] font-semibold shadow-sm'
              : item.highlight
                ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30 hover:text-red-300 border border-red-600/50'
                : alertDot(item.to)
                  ? 'bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 border border-red-500/20'
                  : 'text-slate-300/70 hover:bg-[#1c2a52] hover:text-slate-100'
          );
        }}
      >
        {() => {
          const isActive =
            location.pathname === item.to ||
            location.pathname.startsWith(item.to + '/') ||
            !!item.activePaths?.some((p) => location.pathname.startsWith(p));
          return (
          <>
            <item.icon
              className={cn(
                'w-5 h-5 shrink-0 transition-colors',
                isActive ? 'text-[hsl(228,60%,15%)]'
                  : item.highlight ? 'text-red-400'
                  : alertDot(item.to) ? 'text-red-400'
                  : 'text-slate-400 group-hover:text-slate-100'
              )}
            />
            {!collapsed && (
              <span className="text-sm truncate flex-1">{item.label}</span>
            )}
            {alertDot(item.to) && (
              <span className={cn(
                'inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0',
                collapsed && 'absolute top-1.5 right-1.5',
              )} />
            )}
          </>
          );
        }}
      </NavLink>
    );
  };

  return (
    <aside
      className={cn(
        'flex flex-col shrink-0 h-full transition-all duration-200 relative',
        'bg-[#131d42] text-slate-200 border-r border-[#1c2a52]',
        collapsed ? 'w-[72px]' : 'w-64'
      )}
    >
      {/* Header / Logo */}
      <div className={cn('flex items-center h-16 border-b border-[#1c2a52]', collapsed ? 'justify-center px-2' : 'px-4')}>
        {collapsed ? (
          <img
            src="/Logo-corporacionASI-150x89.png"
            alt="ASI"
            className="h-9 w-9 object-contain"
          />
        ) : (
          <img
            src="/logo_dark.png"
            alt="Corporación ASI"
            className="h-11 w-auto object-contain"
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5 scrollbar-pretty">
        {navItems.map((item) => renderNavItem(item))}

        {/* System Section */}
        {systemNavItems.some(itemVisible) && (
          <>
            <div className={cn(
              'pt-4 pb-2',
              collapsed ? 'flex justify-center' : 'px-3'
            )}>
              {collapsed ? (
                <Settings className="w-4 h-4 text-slate-500" />
              ) : (
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  Sistema
                </span>
              )}
            </div>
            {systemNavItems.filter(itemVisible).map((item) => renderNavItem(item))}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-[#1c2a52] p-2 space-y-2">
        {/* ARC Connection Status */}
        <div className={cn('flex justify-center', collapsed ? 'px-0' : 'px-1')}>
          <ArcConnectionStatus showEventCount={!collapsed} />
        </div>

        {/* User Info (prominent) */}
        {!collapsed && (
          <div className="mx-1 px-3 py-2 rounded-lg bg-slate-700/50 border border-slate-600/50">
            <p className="text-sm font-medium text-slate-100 truncate">
              {profile?.full_name ?? 'Usuario'}
            </p>
            <p className="text-xs text-slate-400 truncate">
              {profile?.email ?? ''}
            </p>
            <button
              onClick={() => setChangePasswordOpen(true)}
              className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-100 transition-colors"
            >
              <KeyRound className="w-3 h-3" />
              Cambiar contraseña
            </button>
          </div>
        )}

        {/* Logout Button (prominent) */}
        {!collapsed && (
          <button
            onClick={signOut}
            className="w-full flex items-center justify-center gap-2 rounded-lg py-2.5 px-3 text-amber-400 bg-amber-400/10 hover:bg-amber-400/20 hover:text-amber-300 border border-amber-400/30 transition-all font-medium text-sm"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        )}

        {/* Theme Toggle */}
        <div className="flex justify-center">
          <ThemeToggle />
        </div>
      </div>

      {/* Collapse toggle (floating outside sidebar) */}
      <button
        onClick={toggle}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-12 bg-[#131d42] border border-[#1c2a52] rounded-r-lg flex items-center justify-center text-slate-400 hover:text-slate-100 hover:bg-[#1c2a52] transition-all shadow-lg z-10"
        title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
      >
        {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
      </button>

      {changePasswordOpen && (
        <ChangePasswordModal onClose={() => setChangePasswordOpen(false)} />
      )}
    </aside>
  );
}
